import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, FunnelChart, Funnel, LabelList
} from 'recharts';
import { 
  Users, TrendingDown, TrendingUp, AlertCircle, Phone, CheckCircle2,
  UserCheck, Award, ArrowRight, Target, MessageCircle
} from 'lucide-react';
import THRDistrictDetailModal from '../../components/THRDistrictDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const EnrollmentDropoutDashboard = () => {
  const [activeTab, setActiveTab] = useState('enrollment');
  const [loading, setLoading] = useState(true);
  
  // State for all data
  const [enrollmentOverview, setEnrollmentOverview] = useState(null);
  const [enrollmentByDistrict, setEnrollmentByDistrict] = useState([]);
  const [enrollmentBySector, setEnrollmentBySector] = useState([]);
  const [enrollmentByType, setEnrollmentByType] = useState([]);
  const [dropoffOverview, setDropoffOverview] = useState(null);
  const [dropoffByType, setDropoffByType] = useState([]);
  const [dropoffBySector, setDropoffBySector] = useState([]);
  const [highRiskBeneficiaries, setHighRiskBeneficiaries] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reengagementOverview, setReengagementOverview] = useState(null);
  const [reengagementBySector, setReengagementBySector] = useState([]);
  const [contactableList, setContactableList] = useState([]);
  const [graduationOverview, setGraduationOverview] = useState(null);
  const [graduationPipeline, setGraduationPipeline] = useState([]);
  const [upcomingGraduations, setUpcomingGraduations] = useState([]);
  const [lifecycleTransitions, setLifecycleTransitions] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        enrollOverview, enrollDistrict, enrollSector, enrollType,
        dropoffOv, dropoffType, dropoffSector, highRisk,
        reengage, reengageSector, contactable,
        gradOverview, gradPipeline, upcomingGrad, lifecycle
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/enrollment/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/enrollment/by-district`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/enrollment/by-sector`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/enrollment/by-type`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/dropoff/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/dropoff/by-type`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/dropoff/by-sector`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/dropoff/high-risk-beneficiaries?limit=50`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/reengagement/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/reengagement/by-sector`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/reengagement/contactable-list?limit=50`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/graduation/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/graduation/pipeline`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/graduation/upcoming-list?limit=50`),
        axios.get(`${BACKEND_URL}/api/dashboards/enrollment-dropout/lifecycle/transitions`)
      ]);

      setEnrollmentOverview(enrollOverview.data);
      setEnrollmentByDistrict(enrollDistrict.data);
      setEnrollmentBySector(enrollSector.data);
      setEnrollmentByType(enrollType.data);
      setDropoffOverview(dropoffOv.data);
      setDropoffByType(dropoffType.data);
      setDropoffBySector(dropoffSector.data);
      setHighRiskBeneficiaries(highRisk.data);
      setReengagementOverview(reengage.data);
      setReengagementBySector(reengageSector.data);
      setContactableList(contactable.data);
      setGraduationOverview(gradOverview.data);
      setGraduationPipeline(gradPipeline.data);
      setUpcomingGraduations(upcomingGrad.data);
      setLifecycleTransitions(lifecycle.data);
    } catch (error) {
      console.error('Error fetching Enrollment & Dropout data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading Enrollment & Drop-off Dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8 pt-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Beneficiary Enrollment & Drop-off Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive lifecycle management tracking enrollment, retention, drop-off patterns, and re-engagement strategies
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="enrollment">Enrollment Overview</TabsTrigger>
              <TabsTrigger value="dropoff">Drop-off Analysis</TabsTrigger>
              <TabsTrigger value="reengagement">Re-engagement</TabsTrigger>
              <TabsTrigger value="graduation">Graduation & Lifecycle</TabsTrigger>
            </TabsList>

            {/* PAGE 1: ENROLLMENT OVERVIEW & TRENDS */}
            <TabsContent value="enrollment" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Beneficiaries</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {enrollmentOverview?.total_beneficiaries?.toLocaleString()}
                        </p>
                      </div>
                      <Users className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Enrollment Rate</p>
                        <p className="text-2xl font-bold text-green-600">
                          {enrollmentOverview?.enrollment_rate}%
                        </p>
                      </div>
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Active Monitoring</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {enrollmentOverview?.monitoring_rate}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {enrollmentOverview?.monitored_beneficiaries?.toLocaleString()} monitored
                        </p>
                      </div>
                      <UserCheck className="h-8 w-8 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Mobile Verified</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {enrollmentOverview?.mobile_verified_pct}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {enrollmentOverview?.mobile_verified?.toLocaleString()} contactable
                        </p>
                      </div>
                      <Phone className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Enrollment by Type */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Enrollment by Beneficiary Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={enrollmentOverview?.by_type || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => {
                            const total = (enrollmentOverview?.by_type || []).reduce((sum, e) => sum + e.count, 0);
                            const percent = total > 0 ? ((entry.count / total) * 100) : 0;
                            // Only show label if percentage is >= 5% to avoid overlapping
                            if (percent >= 5) {
                              return `${entry._id.split('_').pop()}: ${entry.count}`;
                            }
                            return '';
                          }}
                          outerRadius={90}
                          innerRadius={0}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {(enrollmentOverview?.by_type || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconSize={12}
                          style={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Retention by Beneficiary Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart 
                        data={enrollmentByType}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="beneficiary_type" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fontSize: 10 }}
                          interval={0}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          width={60}
                        />
                        <Tooltip 
                          contentStyle={{ fontSize: '12px' }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '10px' }}
                          iconSize={12}
                          style={{ fontSize: '12px' }}
                        />
                        <Bar dataKey="monitored" fill="#10b981" name="Monitored" />
                        <Bar dataKey="drop_off" fill="#ef4444" name="Drop-off" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Enrollment by Sector */}
              <Card>
                <CardHeader>
                  <CardTitle>Enrollment & Retention by Sector</CardTitle>
                  <CardDescription>Sectors sorted by retention rate (lowest first) - Click on a bar to view district details</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      data={enrollmentBySector} 
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        type="number" 
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        dataKey="sector" 
                        type="category" 
                        width={140}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '12px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        iconSize={12}
                        style={{ fontSize: '12px' }}
                      />
                      <Bar 
                        dataKey="monitored" 
                        fill="#10b981" 
                        name="Monitored"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            if (payload?.district_id || payload?.district || payload?.sector) {
                              const districtId = payload.district_id || payload.district || payload.sector;
                              setSelectedDistrict({ district: districtId, district_id: districtId });
                              setIsModalOpen(true);
                            }
                          };

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? '#059669' : '#10b981'}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      />
                      <Bar 
                        dataKey="drop_off" 
                        fill="#ef4444" 
                        name="Drop-off"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            if (payload?.district_id || payload?.district || payload?.sector) {
                              const districtId = payload.district_id || payload.district || payload.sector;
                              setSelectedDistrict({ district: districtId, district_id: districtId });
                              setIsModalOpen(true);
                            }
                          };

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? '#dc2626' : '#ef4444'}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Verification Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Mobile Verification Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Verified</span>
                        <span className="font-bold text-green-600">
                          {enrollmentOverview?.mobile_verified?.toLocaleString()} ({enrollmentOverview?.mobile_verified_pct}%)
                        </span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-green-600 h-3 rounded-full"
                          style={{ width: `${enrollmentOverview?.mobile_verified_pct}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        📱 {enrollmentOverview?.mobile_verified?.toLocaleString()} beneficiaries contactable via mobile
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Aadhaar Verification Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Verified</span>
                        <span className="font-bold text-blue-600">
                          {enrollmentOverview?.aadhaar_verified?.toLocaleString()} ({enrollmentOverview?.aadhaar_verified_pct}%)
                        </span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-blue-600 h-3 rounded-full"
                          style={{ width: `${enrollmentOverview?.aadhaar_verified_pct}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        🆔 {enrollmentOverview?.aadhaar_verified?.toLocaleString()} beneficiaries Aadhaar verified
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* PAGE 2: DROP-OFF ANALYSIS */}
            <TabsContent value="dropoff" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Total Drop-offs</p>
                      <p className="text-3xl font-bold text-red-600">
                        {dropoffOverview?.drop_off_count?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Drop-off Rate</p>
                      <p className="text-3xl font-bold text-orange-600">
                        {dropoffOverview?.drop_off_rate}%
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Retention Rate</p>
                      <p className="text-3xl font-bold text-green-600">
                        {dropoffOverview?.retention_rate}%
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">High Risk</p>
                      <p className="text-3xl font-bold text-red-700">
                        {dropoffOverview?.high_risk_count?.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {dropoffOverview?.high_risk_pct}% of total
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Drop-off by Type */}
              <Card>
                <CardHeader>
                  <CardTitle>Drop-off Analysis by Beneficiary Type</CardTitle>
                  <CardDescription>Retention vs Drop-off comparison</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={dropoffByType}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="beneficiary_type"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="retention_rate" fill="#10b981" name="Retention Rate %" />
                      <Bar dataKey="drop_off_rate" fill="#ef4444" name="Drop-off Rate %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Drop-off Hotspots */}
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-800">
                    <AlertCircle className="h-5 w-5" />
                    Drop-off Hotspot Sectors
                  </CardTitle>
                  <CardDescription>Sectors with highest drop-off rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={dropoffBySector.slice(0, 10)} 
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <YAxis 
                        dataKey="sector" 
                        type="category" 
                        width={120}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '12px' }}
                      />
                      <Bar 
                        dataKey="drop_off_rate" 
                        fill="#f59e0b" 
                        name="Drop-off Rate %"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            if (payload?.district_id || payload?.district || payload?.sector) {
                              const districtId = payload.district_id || payload.district || payload.sector;
                              setSelectedDistrict({ district: districtId, district_id: districtId });
                              setIsModalOpen(true);
                            }
                          };

                          const fillColor = payload?.drop_off_rate > 50 ? '#ef4444' : '#f59e0b';
                          const hoverColor = payload?.drop_off_rate > 50 ? '#dc2626' : '#d97706';

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? hoverColor : fillColor}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      >
                        {dropoffBySector.slice(0, 10).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.drop_off_rate > 50 ? '#ef4444' : '#f59e0b'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* High Risk Beneficiaries Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-red-600" />
                    High-Risk Beneficiaries (Top 20)
                  </CardTitle>
                  <CardDescription>Beneficiaries at immediate risk of drop-off requiring urgent intervention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Name</th>
                          <th className="px-4 py-3 text-center font-semibold">Type</th>
                          <th className="px-4 py-3 text-center font-semibold">Age (months)</th>
                          <th className="px-4 py-3 text-left font-semibold">Sector</th>
                          <th className="px-4 py-3 text-center font-semibold">Mobile</th>
                          <th className="px-4 py-3 text-center font-semibold">Aadhaar</th>
                          <th className="px-4 py-3 text-center font-semibold">Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {highRiskBeneficiaries.slice(0, 20).map((ben, index) => (
                          <tr 
                            key={index} 
                            className="border-b hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              if (ben.district_id || ben.district || ben.sector) {
                                const districtId = ben.district_id || ben.district || ben.sector;
                                setSelectedDistrict({ district: districtId, district_id: districtId });
                                setIsModalOpen(true);
                              }
                            }}
                          >
                            <td className="px-4 py-3 font-medium">{ben.beneficiary_name || 'N/A'}</td>
                            <td className="px-4 py-3 text-center text-xs">
                              {ben.beneficiary_type?.replace('children_', '').replace('0m_6m', '0-6m').replace('6m_3y', '6m-3y').replace('3y_6y', '3-6y')}
                            </td>
                            <td className="px-4 py-3 text-center">{ben.age_months || 'N/A'}</td>
                            <td className="px-4 py-3">{ben.sector}</td>
                            <td className="px-4 py-3 text-center">
                              {ben.mobile_verified ? (
                                <span className="text-green-600">✓</span>
                              ) : (
                                <span className="text-red-600">✗</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {ben.aadhaar_verified ? (
                                <span className="text-green-600">✓</span>
                              ) : (
                                <span className="text-red-600">✗</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs ${ben.reengagement_priority >= 5 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                                {ben.reengagement_priority}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* PAGE 3: RE-ENGAGEMENT & RETENTION */}
            <TabsContent value="reengagement" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Drop-offs</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {reengagementOverview?.total_drop_off?.toLocaleString()}
                        </p>
                      </div>
                      <Users className="h-8 w-8 text-gray-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Contactable</p>
                        <p className="text-2xl font-bold text-green-600">
                          {reengagementOverview?.contactable?.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {reengagementOverview?.contactable_pct}% reachable
                        </p>
                      </div>
                      <Phone className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">High Priority</p>
                        <p className="text-2xl font-bold text-orange-600">
                          {reengagementOverview?.high_priority?.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {reengagementOverview?.high_priority_pct}% urgent
                        </p>
                      </div>
                      <Target className="h-8 w-8 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Aadhaar Verified</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {reengagementOverview?.aadhaar_verified?.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {reengagementOverview?.aadhaar_verified_pct}% traceable
                        </p>
                      </div>
                      <CheckCircle2 className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Re-engagement Readiness */}
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-green-900">📱 SMS Campaign Readiness</CardTitle>
                  <CardDescription>Mobile-verified beneficiaries ready for outreach</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-lg">
                      <div className="text-center">
                        <MessageCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                        <p className="text-2xl font-bold text-green-700 mb-2">
                          {reengagementOverview?.contactable?.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600">SMS Campaign Ready</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg">
                      <div className="text-center">
                        <Target className="h-12 w-12 text-orange-600 mx-auto mb-3" />
                        <p className="text-2xl font-bold text-orange-700 mb-2">
                          {reengagementOverview?.high_priority?.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600">High Priority Targets</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg">
                      <div className="text-center">
                        <Phone className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                        <p className="text-2xl font-bold text-blue-700 mb-2">
                          {reengagementOverview?.contactable_pct}%
                        </p>
                        <p className="text-sm text-gray-600">Outreach Success Rate</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Re-engagement by Sector */}
              <Card>
                <CardHeader>
                  <CardTitle>Re-engagement Opportunities by Sector</CardTitle>
                  <CardDescription>Campaign targeting priorities</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart 
                      data={reengagementBySector}
                      margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="sector" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        tick={{ fontSize: 10 }}
                        interval={0}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        width={60}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '12px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        iconSize={12}
                        style={{ fontSize: '12px' }}
                      />
                      <Bar 
                        dataKey="contactable" 
                        fill="#10b981" 
                        name="Contactable"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            if (payload?.district_id || payload?.district || payload?.sector) {
                              const districtId = payload.district_id || payload.district || payload.sector;
                              setSelectedDistrict({ district: districtId, district_id: districtId });
                              setIsModalOpen(true);
                            }
                          };

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? '#059669' : '#10b981'}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      />
                      <Bar 
                        dataKey="high_priority" 
                        fill="#f59e0b" 
                        name="High Priority"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            if (payload?.district_id || payload?.district || payload?.sector) {
                              const districtId = payload.district_id || payload.district || payload.sector;
                              setSelectedDistrict({ district: districtId, district_id: districtId });
                              setIsModalOpen(true);
                            }
                          };

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? '#d97706' : '#f59e0b'}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Contactable Beneficiaries List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-green-600" />
                    Contactable Beneficiaries for Re-engagement (Top 20)
                  </CardTitle>
                  <CardDescription>Mobile-verified beneficiaries ready for SMS/call campaigns</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Name</th>
                          <th className="px-4 py-3 text-center font-semibold">Type</th>
                          <th className="px-4 py-3 text-left font-semibold">Sector</th>
                          <th className="px-4 py-3 text-left font-semibold">AWC</th>
                          <th className="px-4 py-3 text-center font-semibold">Aadhaar</th>
                          <th className="px-4 py-3 text-center font-semibold">Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactableList.slice(0, 20).map((ben, index) => (
                          <tr 
                            key={index} 
                            className="border-b hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              if (ben.district_id || ben.district || ben.sector) {
                                const districtId = ben.district_id || ben.district || ben.sector;
                                setSelectedDistrict({ district: districtId, district_id: districtId });
                                setIsModalOpen(true);
                              }
                            }}
                          >
                            <td className="px-4 py-3 font-medium">{ben.beneficiary_name || 'N/A'}</td>
                            <td className="px-4 py-3 text-center text-xs">
                              {ben.beneficiary_type?.replace('children_', '').replace('0m_6m', '0-6m').replace('6m_3y', '6m-3y').replace('3y_6y', '3-6y')}
                            </td>
                            <td className="px-4 py-3">{ben.sector}</td>
                            <td className="px-4 py-3 text-xs">{ben.awc_name}</td>
                            <td className="px-4 py-3 text-center">
                              {ben.aadhaar_verified ? (
                                <span className="text-green-600">✓</span>
                              ) : (
                                <span className="text-gray-400">✗</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs ${ben.reengagement_priority >= 5 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                {ben.reengagement_priority}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* PAGE 4: GRADUATION & LIFECYCLE MANAGEMENT */}
            <TabsContent value="graduation" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Award className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-600 mb-2">Near Graduation</p>
                      <p className="text-3xl font-bold text-purple-600">
                        {graduationOverview?.near_graduation?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <ArrowRight className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-600 mb-2">Transition Pending</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {graduationOverview?.transition_pending?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Users className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-600 mb-2">Children 3-6y</p>
                      <p className="text-3xl font-bold text-green-600">
                        {graduationOverview?.children_3_6y?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <TrendingUp className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-600 mb-2">Expected (3 months)</p>
                      <p className="text-3xl font-bold text-orange-600">
                        {graduationOverview?.expected_transitions_3months?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Lifecycle Pipeline */}
              <Card>
                <CardHeader>
                  <CardTitle>Beneficiary Lifecycle Pipeline</CardTitle>
                  <CardDescription>Distribution across lifecycle stages</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={graduationPipeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="lifecycle_stage" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" name="Beneficiaries" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Lifecycle Transitions */}
              <Card>
                <CardHeader>
                  <CardTitle>Lifecycle Transitions by Type</CardTitle>
                  <CardDescription>Category-wise transition planning</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {lifecycleTransitions.map((transition, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">
                            {transition.beneficiary_type?.replace('_', ' ')}
                          </h4>
                          <span className="text-sm font-bold text-purple-600">
                            {transition.graduation_pct}% near graduation
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Total</p>
                            <p className="font-bold">{transition.total?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Active</p>
                            <p className="font-bold text-green-600">{transition.active?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Near Graduation</p>
                            <p className="font-bold text-purple-600">{transition.near_graduation?.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Graduations Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-purple-600" />
                    Upcoming Graduations (Top 20)
                  </CardTitle>
                  <CardDescription>Beneficiaries approaching program completion</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Name</th>
                          <th className="px-4 py-3 text-center font-semibold">Type</th>
                          <th className="px-4 py-3 text-center font-semibold">Age (months)</th>
                          <th className="px-4 py-3 text-left font-semibold">Sector</th>
                          <th className="px-4 py-3 text-left font-semibold">AWC</th>
                          <th className="px-4 py-3 text-center font-semibold">Service Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {upcomingGraduations.slice(0, 20).map((ben, index) => (
                          <tr 
                            key={index} 
                            className="border-b hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              if (ben.district_id || ben.district || ben.sector) {
                                const districtId = ben.district_id || ben.district || ben.sector;
                                setSelectedDistrict({ district: districtId, district_id: districtId });
                                setIsModalOpen(true);
                              }
                            }}
                          >
                            <td className="px-4 py-3 font-medium">{ben.beneficiary_name || 'N/A'}</td>
                            <td className="px-4 py-3 text-center text-xs">
                              {ben.beneficiary_type?.replace('children_', '').replace('3y_6y', '3-6y')}
                            </td>
                            <td className="px-4 py-3 text-center font-bold">{ben.age_months}</td>
                            <td className="px-4 py-3">{ben.sector}</td>
                            <td className="px-4 py-3 text-xs">{ben.awc_name}</td>
                            <td className="px-4 py-3 text-center">{ben.service_duration_days || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* District Detail Modal */}
      <THRDistrictDetailModal
        district={selectedDistrict}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDistrict(null);
        }}
        reportingPeriod="Oct-2025"
        dashboardType="enrollment-dropout"
      />
    </div>
  );
};

export default EnrollmentDropoutDashboard;
